import React, { FC, useState } from 'react';
import toast from 'react-hot-toast';
import DialogLayout from '../DialogLayout';
import Typography from '../../components/Typography/Typography';
import ASSETS from '../../assets';
import Button from '../../components/Button/Button';
import { authAPI, MismatchedUser } from '../../services/api';

interface IProps {
  open: boolean;
  setOpen: React.Dispatch<React.SetStateAction<boolean>>;
  user: MismatchedUser | null;
  onSuccess: () => void;
}

const ResetWorldIdVerification: FC<IProps> = ({ open, setOpen, user, onSuccess }) => {
  const [loading, setLoading] = useState(false);

  const handleReset = async () => {
    if (!user) return;

    try {
      setLoading(true);

      const response = await authAPI.resetUserWorldIdVerification({
        id: String(user.id),
        email: user.email,
      });

      toast.success(response.message || 'User world id verification has been reset.');
      handleClose();
      onSuccess();
    } catch (err: any) {
      let errorMessage = 'Failed to reset world id verification. Please try again.';

      if (err.response?.data?.message) {
        errorMessage = err.response.data.message;
      } else if (err.response?.status === 400) {
        errorMessage = 'This mismatch has already been resolved.';
      } else if (err.response?.status === 500) {
        errorMessage = 'Server error. Please try again later.';
      } else if (err.message) {
        errorMessage = err.message;
      }

      toast.error(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    setOpen(false);
  };

  return (
    <DialogLayout open={open}>
      <div className="w-full max-w-[536px] p-[25px] bg-white rounded-[15px] relative">
        <div
          className="absolute top-[25px] right-[25px] rounded-full size-[30px] bg-[#0000000D] cursor-pointer grid place-items-center"
          onClick={handleClose}
        >
          <img src={ASSETS.cross} alt="" />
        </div>
        <Typography variant="h2" className="font-bold title">
          Reset World ID Verification
        </Typography>
        <Typography variant="body1" color="grey" className="mt-[5px] font-medium">
          Are you sure you want to reset this user&apos;s World ID verification? This action cannot be undone.
        </Typography>

        <div className="border border-k-border rounded-lg p-6 mt-[25px] flex flex-col gap-6">
          <div className="bg-gray-50 p-4 rounded-lg flex flex-col gap-3">
            <div>
              <Typography variant="body2" className="text-gray-600 mb-1">
                Email Address:
              </Typography>
              <Typography variant="body1" className="font-medium text-gray-800">
                {user?.email}
              </Typography>
            </div>
            {(user?.firstName || user?.lastName) && (
              <div>
                <Typography variant="body2" className="text-gray-600 mb-1">
                  Name:
                </Typography>
                <Typography variant="body1" className="font-medium text-gray-800">
                  {[user?.firstName, user?.lastName].filter(Boolean).join(' ')}
                </Typography>
              </div>
            )}
          </div>

          <div className="flex gap-3">
            <Button
              label="Cancel"
              variant="outlined"
              className="flex-1 h-[45px]"
              onClick={handleClose}
              disabled={loading}
              type="button"
            />
            <Button
              label={loading ? 'Resetting...' : 'Confirm Reset'}
              variant="primary"
              className="flex-1 h-[45px] disabled:opacity-200 disabled:cursor-not-allowed disabled:!text-gray-400"
              onClick={handleReset}
              loading={loading}
              disabled={loading}
              type="button"
            />
          </div>
        </div>
      </div>
    </DialogLayout>
  );
};

export default ResetWorldIdVerification;
